import axios from "axios"
import { keys } from "../keys"
import { useErrorOverlayStore, type ErrorEntry } from "../stores/error-overlay-store"

const SERVICE_NAME = "admin"
const FLUSH_DELAY = 3000
const FLUSH_SIZE = 10

let buffer: ErrorEntry[] = []
let sentIds = new Set<string>()
let sentMessages = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | null = null

export interface DeviceInfo {
  browser: string
  os: string
  screenWidth: number
  screenHeight: number
  viewportWidth: number
  viewportHeight: number
  language: string
  cores?: number
  memory?: number
}

let cachedDevice: DeviceInfo | null = null

function getDeviceInfo(): DeviceInfo {
  if (cachedDevice) return cachedDevice

  const ua = navigator.userAgent

  // Parse browser
  let browser = "Unknown"
  if (ua.includes("Firefox/")) {
    browser = "Firefox " + (ua.match(/Firefox\/(\d+)/)?.[1] ?? "")
  } else if (ua.includes("Edg/")) {
    browser = "Edge " + (ua.match(/Edg\/(\d+)/)?.[1] ?? "")
  } else if (ua.includes("Chrome/")) {
    browser = "Chrome " + (ua.match(/Chrome\/(\d+)/)?.[1] ?? "")
  } else if (ua.includes("Safari/") && !ua.includes("Chrome")) {
    browser = "Safari " + (ua.match(/Version\/(\d+)/)?.[1] ?? "")
  }

  // Parse OS
  let os = "Unknown"
  if (ua.includes("Mac OS X")) {
    os = "macOS " + (ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, ".") ?? "")
  } else if (ua.includes("Windows NT")) {
    const ntVersion = ua.match(/Windows NT ([\d.]+)/)?.[1]
    const winMap: Record<string, string> = { "10.0": "10/11", "6.3": "8.1", "6.2": "8", "6.1": "7" }
    os = "Windows " + (ntVersion ? winMap[ntVersion] ?? ntVersion : "")
  } else if (ua.includes("Android")) {
    os = "Android " + (ua.match(/Android ([\d.]+)/)?.[1] ?? "")
  } else if (ua.includes("iPhone OS") || ua.includes("iPad")) {
    os = "iOS " + (ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, ".") ?? "")
  } else if (ua.includes("Linux")) {
    os = "Linux"
  }

  const nav = navigator as Navigator & { deviceMemory?: number }

  cachedDevice = {
    browser: browser.trim(),
    os: os.trim(),
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    language: navigator.language,
    cores: navigator.hardwareConcurrency || undefined,
    memory: nav.deviceMemory || undefined,
  }

  return cachedDevice
}

function mapError(e: ErrorEntry) {
  return {
    message: e.message,
    source: e.source,
    stack: e.stack,
    componentStack: e.componentStack,
    url: e.url,
    fileLocation: e.fileLocation,
    userAgent: e.userAgent,
    userId: e.userId,
    count: e.count,
    timestamp: e.timestamp,
  }
}

// Same message within a 2s window = same error caught by multiple handlers (react + runtime)
function dedupeKey(e: ErrorEntry): string {
  return `${e.message}::${Math.floor(e.timestamp / 2000)}`
}

function flush() {
  if (buffer.length === 0) return

  const toSend = buffer.splice(0)

  axios
    .post(
      `${keys.API_URL}/v1/system/logs_frontend`,
      {
        service: SERVICE_NAME,
        device: getDeviceInfo(),
        errors: toSend.map(mapError),
      },
      {
        headers: { authorization: `Bearer ${keys.SYSTEM_API_KEY}` },
        withCredentials: true,
        timeout: 10000,
      },
    )
    .catch(() => {})
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer)
  flushTimer = setTimeout(flush, FLUSH_DELAY)
}

export function initErrorReporter() {
  useErrorOverlayStore.subscribe((state) => {
    const newErrors = state.errors.filter((e) => !sentIds.has(e.id))

    for (const error of newErrors) {
      sentIds.add(error.id)

      // Skip if we already buffered/sent an error with the same message in the same 2s window
      const key = dedupeKey(error)
      if (sentMessages.has(key)) continue
      sentMessages.add(key)

      // Don't send chunk loading errors to system logs
      if (
        error.message.includes("Loading chunk") ||
        error.message.includes("Failed to fetch dynamically imported module") ||
        error.message.includes("Importing a module script failed") ||
        error.message.includes("ChunkLoadError")
      ) continue

      buffer.push(error)
    }

    if (buffer.length === 0) return

    if (buffer.length >= FLUSH_SIZE) {
      if (flushTimer) clearTimeout(flushTimer)
      flush()
    } else {
      scheduleFlush()
    }
  })

  window.addEventListener("beforeunload", () => {
    if (buffer.length === 0) return

    const payload = JSON.stringify({
      service: SERVICE_NAME,
      device: getDeviceInfo(),
      errors: buffer.map(mapError),
    })

    navigator.sendBeacon(
      `${keys.API_URL}/v1/system/logs_frontend`,
      new Blob([payload], { type: "application/json" }),
    )

    buffer = []
  })
}
